import { isDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';

import type {
  BetterFetchLogger,
  BetterFetch,
  BetterFetchFactoryResult,
  BaseConfig,
  RetryContext,
  RetryDecision,
  RetryStrategyFn,
} from '../types/better-fetch.type';

/**
 * HTTP error with status code information
 */
class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/**
 * Default retryable HTTP status codes
 */
const DEFAULT_RETRYABLE_STATUS_CODES = [429, 500, 502, 503, 504] as const;

/**
 * Maximum retry delay (30 seconds)
 */
const DEFAULT_MAX_RETRY_DELAY_MS = 30_000;

/**
 * Parse Retry-After header value
 * @param value Header value (either seconds as number or HTTP date)
 * @returns Delay in milliseconds
 */
const parseRetryAfter = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  // Try parsing as number (seconds)
  const seconds = Number.parseInt(value, 10);
  if (!Number.isNaN(seconds) && seconds > 0) {
    return seconds * 1_000;
  }

  // Try parsing as HTTP date
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    const delayMs = date.getTime() - Date.now();
    return delayMs > 0 ? delayMs : undefined;
  }

  return undefined;
};

/**
 * Default retry strategy
 * - Retries on: network errors, timeouts, 429, 500, 502, 503, 504
 * - Uses exponential backoff with max delay of 30s
 * - Respects Retry-After header when present
 */
export const defaultRetryStrategy: RetryStrategyFn = ({
  statusCode,
  attempt,
  error,
  retryAfterMs,
}: RetryContext): RetryDecision => {
  // Handle network errors and timeouts (always retry)
  if (
    error instanceof TypeError ||
    (isDefined(error) &&
      typeof error === 'object' &&
      'name' in error &&
      error.name === 'AbortError')
  ) {
    const baseDelayMs = 500;
    const exponentialDelay = baseDelayMs * 2 ** attempt;
    return {
      shouldRetry: true,
      delayMs: Math.min(exponentialDelay, DEFAULT_MAX_RETRY_DELAY_MS),
    };
  }

  // Handle HTTP errors
  if (isDefined(statusCode)) {
    const isRetryable = (
      DEFAULT_RETRYABLE_STATUS_CODES as readonly number[]
    ).includes(statusCode);

    if (!isRetryable) {
      return { shouldRetry: false, delayMs: 0 };
    }

    // Use Retry-After header if present
    if (isDefined(retryAfterMs)) {
      return {
        shouldRetry: true,
        delayMs: Math.min(retryAfterMs, DEFAULT_MAX_RETRY_DELAY_MS),
      };
    }

    // Otherwise use exponential backoff
    const baseDelayMs = 500;
    const exponentialDelay = baseDelayMs * 2 ** attempt;
    return {
      shouldRetry: true,
      delayMs: Math.min(exponentialDelay, DEFAULT_MAX_RETRY_DELAY_MS),
    };
  }

  // Don't retry other errors
  return { shouldRetry: false, delayMs: 0 };
};

export const betterFetch: BetterFetch = async <T>(
  url: string,
  options: RequestInit = {},
  {
    logger = console,
    retries = 3,
    timeoutMs = 5_000,
    retryStrategy = defaultRetryStrategy,
  }: {
    logger?: BetterFetchLogger;
    retries?: number;
    timeoutMs?: number;
    retryStrategy?: RetryStrategyFn;
  } = {},
): Promise<T> => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => {
        controller.abort();
      }, timeoutMs);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(id);

      if (!response.ok) {
        const text = await response.text().catch(() => EMPTY_STRING);
        const message = `HTTP ${response.status.toFixed()}: ${text || response.statusText} - URL: ${url}`;
        logger.error(message);

        // Always extract Retry-After header (strategy will decide whether to use it)
        const retryAfterMs = parseRetryAfter(
          response.headers.get('Retry-After'),
        );

        throw new HttpError(message, response.status, retryAfterMs);
      }

      try {
        return (await response.json()) as T;
      } catch {
        const message = `Invalid response (unable to parse JSON) - URL: ${url}`;
        logger.error(message);
        throw new Error(message);
      }
    } catch (err: unknown) {
      const isLastAttempt = attempt === retries;

      // Prepare error message for logging
      const errMessage = err instanceof Error ? err.message : 'Unknown error';

      // Build retry context
      const context: RetryContext = {
        statusCode: err instanceof HttpError ? err.status : undefined,
        attempt,
        error: err instanceof Error ? err : new Error(String(err)),
        retryAfterMs: err instanceof HttpError ? err.retryAfterMs : undefined,
      };

      // Get retry decision from strategy
      const decision = retryStrategy(context);

      if (!decision.shouldRetry || isLastAttempt) {
        const message = `Final failure (${(attempt + 1).toFixed()}/${(retries + 1).toFixed()}): ${errMessage} - URL: ${url}`;
        logger.error(message);
        throw new Error(message);
      }

      const message = `Error: ${errMessage}, retrying in ${decision.delayMs.toFixed()}ms... - URL: ${url}`;
      logger.error(message);
      await new Promise((res) => setTimeout(res, decision.delayMs));
    }
  }

  const message = `betterFetch: unexpected exit - URL: ${url}`;
  logger.error(message);
  throw new Error(message);
};

export const betterFetchFactory =
  (logger: BetterFetchLogger): BetterFetchFactoryResult =>
  <T>(url: string, options: RequestInit = {}, config: BaseConfig = {}) =>
    betterFetch<T>(url, options, { logger, ...config });
